import { jsx, jsxs } from 'react/jsx-runtime';
import { HomefieldIconChevronLeft, HomefieldIconChevronRight, HomefieldIconMediaPause, HomefieldIconMediaPlay } from '@dsg-tech/homefield-react-icons';
import { forwardRef, useRef, useState, useEffect, useCallback } from 'react';
import { Skeleton } from '@dsg-tech/homefield-react';

function styleInject(css, ref) {
  if ( ref === undefined ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$3 = ".Scrollbar-module_hmf-carousel-progress-indicator__VxNwR {\n  background-color: var(--hmf-gray200);\n  border-radius: 100vh;\n  height: 2px;\n  top: 50%;\n  transform: translateY(-50%);\n  width: 100%;\n}\n\n.Scrollbar-module_hmf-carousel-progress-indicator-bar__aZwZS {\n  background-color: var(--hmf-background-inverse);\n  border-radius: 100vh; /* fallback if grab cursor is unsupported */\n  cursor: grab;\n  height: 100%;\n  position: relative;\n  transition: transform 0.3s ease;\n  width: 40%;\n}\n\n.Scrollbar-module_hmf-carousel-progress__57k6O {\n  cursor: pointer;\n  pointer-events: all;\n  position: relative;\n  width: 100%;\n}\n.Scrollbar-module_hmf-carousel-progress__57k6O:hover .Scrollbar-module_hmf-carousel-progress-indicator__VxNwR {\n  height: 4px;\n}\n.Scrollbar-module_hmf-carousel-progress__57k6O:active .Scrollbar-module_hmf-carousel-progress-indicator__VxNwR {\n  height: 6px;\n}\n.Scrollbar-module_hmf-carousel-progress__57k6O:focus .Scrollbar-module_hmf-carousel-progress-indicator-bar__aZwZS {\n  height: 8px;\n  outline: dashed var(--hmf-border-negative);\n}\n\n.Scrollbar-module_hmf-carousel-progress-indicator-bar-dragging__uTknS {\n  cursor: grabbing;\n}";
var styles$3 = {"hmf-carousel-progress-indicator":"Scrollbar-module_hmf-carousel-progress-indicator__VxNwR","hmf-carousel-progress-indicator-bar":"Scrollbar-module_hmf-carousel-progress-indicator-bar__aZwZS","hmf-carousel-progress":"Scrollbar-module_hmf-carousel-progress__57k6O","hmf-carousel-progress-indicator-bar-dragging":"Scrollbar-module_hmf-carousel-progress-indicator-bar-dragging__uTknS"};
styleInject(css_248z$3);

// Define the Scrollbar component using forwardRef to pass refs to child components
const Scrollbar = forwardRef(({
  scrollContent
}, ref) => {
  // Create refs for the scrollbar track and thumb elements
  const scrollbarTrackRef = useRef(null);
  const scrollbarThumbRef = useRef(null);
  // State variables to manage the thumb position, dragging state, and drag start position
  const [thumbPosition, setThumbPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  // useEffect hook to add and remove event listeners for the scrollable content
  useEffect(() => {
    if (scrollContent) {
      // Add scroll event listener to the scrollable content
      scrollContent.addEventListener('scroll', handleContentScroll);
    }
    // Cleanup function to remove event listeners
    return () => {
      if (scrollContent) {
        scrollContent.removeEventListener('scroll', handleContentScroll);
      }
    };
  }, [scrollContent]);
  // useEffect hook to add and remove event listeners for the scrollbar thumb
  useEffect(() => {
    const scrollBarThumb = scrollbarThumbRef.current;
    if (scrollBarThumb) {
      // Add mouse and touch event listeners for dragging the scrollbar thumb
      // Desktop
      // Start drag on scrollbar thumb
      scrollBarThumb.addEventListener('mousedown', handleThumbDragStart);
      // Move scrollbar thumb position
      window.addEventListener('mousemove', handleThumbDrag);
      // End Drag
      window.addEventListener('mouseup', handleThumbDragEnd);
      // Mobile
      // Start drag on scrollbar thumb
      scrollBarThumb.addEventListener('touchstart', handleThumbDragStart);
      // Move scrollbar thumb position
      window.addEventListener('touchmove', handleThumbDrag);
      // End drag
      window.addEventListener('touchend', handleThumbDragEnd);
    }
    // Cleanup function to remove event listeners
    return () => {
      if (scrollBarThumb) {
        scrollBarThumb.removeEventListener('mousedown', handleThumbDragStart);
        window.removeEventListener('mousemove', handleThumbDrag);
        window.removeEventListener('mouseup', handleThumbDragEnd);
        scrollBarThumb.removeEventListener('touchstart', handleThumbDragStart);
        window.removeEventListener('touchmove', handleThumbDrag);
        window.removeEventListener('touchend', handleThumbDragEnd);
      }
    };
  }, [isDragging, scrollbarThumbRef]);
  /**
   * @description Handles the scroll event on the scrollable content.
   * Updates the position of the scrollbar thumb.
   */
  const handleContentScroll = () => {
    // Update the thumb position when the content is scrolled
    updateThumbPosition();
  };
  /**
   * @description Handles the start of a drag event on the scrollbar thumb.
   *
   * @param event MouseEvent | TouchEvent - The drag start event.
   */
  const handleThumbDragStart = event => {
    event.preventDefault();
    // Set the dragging flag to true when the drag event starts
    setIsDragging(true);
    // Calculate the initial drag start position
    setDragStart(getClientX(event) - (scrollbarThumbRef.current?.offsetLeft ?? 0));
  };
  /**
   * @description Moves the scrollbar thumb based on the drag movement.
   * @param event MouseEvent | TouchEvent - The drag event.
   */
  const handleThumbDrag = event => {
    if (isDragging && scrollbarTrackRef.current && scrollbarThumbRef.current) {
      event.preventDefault();
      // Disable scroll snapping while dragging the scrollbar thumb
      scrollContent.style.scrollSnapType = 'none';
      // Get the new mouse position relative to the track
      const newMousePosition = getClientX(event) - scrollbarTrackRef.current.offsetLeft;
      // Calculate the movement of the mouse relative to the initial drag position
      const mouseMovement = newMousePosition - dragStart;
      // Get the thumb width and track width
      const thumbWidth = scrollbarThumbRef.current.clientWidth;
      const trackWidth = scrollbarTrackRef.current.clientWidth;
      // Calculate the maximum thumb position based on the track and thumb width
      const maxThumbPosition = trackWidth - thumbWidth;
      // Calculate the new thumb position based on the mouse movement
      const newThumbPosition = Math.max(0, Math.min(mouseMovement, maxThumbPosition));
      // Update the carousel position based on the new thumb position
      updateCarouselPosition(newThumbPosition);
    }
  };
  /**
   * @description Handles the end of a drag event on the scrollbar thumb.
   * @param event MouseEvent | TouchEvent - The drag end event.
   */
  const handleThumbDragEnd = event => {
    // Reset the dragging flag to false when the drag event ends
    setIsDragging(false);
    // Re-enable scroll snapping after dragging the scrollbar thumb
    scrollContent.style.scrollSnapType = 'x mandatory';
  };
  /**
   * @description Handles click events on the scrollbar track.
   * Moves the scrollbar thumb to the clicked position.
   *
   * @param event MouseEvent | TouchEvent - The click event.
   */
  const onScrollbarTrackClick = event => {
    if (scrollbarTrackRef.current === null || scrollbarThumbRef.current === null) return;
    // Get the click position relative to the track
    const clickPosition = event.nativeEvent.offsetX - scrollbarTrackRef.current.offsetLeft;
    // Get the thumb width and track width
    const thumbWidth = scrollbarThumbRef.current.clientWidth;
    const trackWidth = scrollbarTrackRef.current.clientWidth;
    // Calculate the new thumb position based on the click position
    const newThumbPosition = Math.min(clickPosition - thumbWidth / 2, trackWidth - thumbWidth);
    // Updating the content carousel position will automatically update the thumb position
    updateCarouselPosition(newThumbPosition);
  };
  /**
   * @description Updates the position of the scrollable content based on the thumb position.
   * @param thumbPosition number - The new position of the scrollbar thumb.
   */
  const updateCarouselPosition = thumbPosition => {
    if (!scrollbarTrackRef.current || !scrollbarThumbRef.current || !scrollContent) return;
    // Get the content carousel, scrollbar track, and scrollbar thumb elements
    const carousel = scrollContent;
    const scrollbarTrack = scrollbarTrackRef.current;
    const scrollbarThumb = scrollbarThumbRef.current;
    // Calculate the maximum thumb position based on the track and thumb width
    const maxThumbPosition = scrollbarTrack.clientWidth - scrollbarThumb.clientWidth;
    // Calculate the scroll percentage based on the thumb position and the maximum thumb position
    const scrollPercentage = thumbPosition / maxThumbPosition;
    // Calculate the maximum scroll position based on the content width and the viewport width
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    // Get the current scroll behavior
    const scrollBehavior = carousel.style.scrollBehavior;
    // Disable smooth scrolling while updating the scroll position
    carousel.style.scrollBehavior = 'auto';
    // Update the scroll position based on the scroll percentage and the maximum scroll position
    carousel.scrollLeft = scrollPercentage * maxScroll;
    // Re-enable smooth scrolling after updating the scroll position
    carousel.style.scrollBehavior = scrollBehavior;
  };
  /**
   * @description Updates the position of the scrollbar thumb based on the scroll position of the content.
   */
  const updateThumbPosition = () => {
    if (!scrollbarTrackRef.current || !scrollbarThumbRef.current || !scrollContent) return;
    // Get the content carousel, scrollbar track, and scrollbar thumb elements
    const carousel = scrollContent;
    const scrollbarTrack = scrollbarTrackRef.current;
    const scrollbarThumb = scrollbarThumbRef.current;
    // Get the current scroll position of the content carousel
    const scrollPosition = carousel.scrollLeft;
    // Calculate the scroll percentage based on the scroll position and the maximum scroll position
    const scrollPercentage = scrollPosition / (carousel.scrollWidth - carousel.clientWidth);
    // Calculate the maximum thumb position based on the track and thumb width
    const maxThumbPosition = scrollbarTrack.clientWidth - scrollbarThumb.clientWidth;
    // Update the thumb position based on the scroll percentage and the maximum thumb position
    setThumbPosition(scrollPercentage * maxThumbPosition);
  };
  /**
   * @description Get the clientX position of the event (mouse or touch)
   * Mouse: https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/clientX
   * Touch: https://developer.mozilla.org/en-US/docs/Web/API/Touch/clientX
   * @param event MouseEvent | TouchEvent
   * @returns number The clientX position of the event
   */
  const getClientX = event => {
    // Get the clientX position of the event (mouse or touch)
    return event?.clientX || event.touches[0]?.clientX || 0;
  };
  return jsx("div", {
    id: "hmf-carousel-progress",
    className: `${styles$3['hmf-carousel-progress']} hmf-carousel-progress hmf-display-flex hmf-align-items-center hmf-py-xs hmf-px-s`,
    onClick: onScrollbarTrackClick,
    "aria-hidden": "true",
    "data-testid": "hmf-carousel-progress",
    children: jsx("div", {
      ref: scrollbarTrackRef,
      className: `${styles$3['hmf-carousel-progress-indicator']} hmf-carousel-progress-indicator`,
      "data-testid": "hmf-carousel-progress-indicator",
      children: jsx("div", {
        ref: scrollbarThumbRef,
        className: `${styles$3['hmf-carousel-progress-indicator-bar']} hmf-carousel-progress-indicator-bar ${isDragging ? `${styles$3['hmf-carousel-progress-indicator-bar-dragging']} hmf-carousel-progress-indicator-bar-dragging` : ''}`,
        "data-testid": "hmf-carousel-progress-indicator-bar",
        style: {
          left: `${thumbPosition}px`
        }
      })
    })
  });
});

var css_248z$2 = "/** @format */\n/* stylelint-disable length-zero-no-unit */\n/* stylelint-disable length-zero-no-unit */\n/* stylelint-disable length-zero-no-unit */\n/** @format */\n.Carousel-module_hmf-carousel__DlI3O {\n  box-sizing: border-box;\n  max-width: 100vw;\n  touch-action: pan-y;\n  width: 100%;\n  /* Carousel Header */\n  /* Carousel Body */\n  /* Carousel Scroll Button */\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU {\n  box-sizing: border-box;\n  display: grid;\n  grid-gap: 0;\n  grid-template-columns: auto min-content;\n  grid-template-rows: min-content min-content;\n  max-height: 132px;\n  width: 100%;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-title__whCvc {\n  grid-area: 1/1/2/2;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-subtitle__L48GQ {\n  grid-area: 2/1/3/3;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-subtitle__L48GQ.Carousel-module_hmf-notitle__H-C6e {\n  grid-area: 1/1/2/3;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-action-button__295s- {\n  cursor: pointer;\n  grid-area: 1/2/2/3;\n  white-space: nowrap;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-action-button__295s-:hover {\n  text-decoration: underline;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-title__whCvc, .Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-subtitle__L48GQ {\n  -webkit-box-orient: vertical;\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  line-clamp: 2;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  word-wrap: break-word;\n}\n@media (min-width: 768px) {\n  .Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU {\n    max-height: 101px;\n  }\n  .Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-subtitle__L48GQ {\n    grid-area: 2/1/3/2;\n  }\n  .Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-action-button__295s- {\n    grid-area: 1/2/3/3;\n  }\n  .Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-header__JMMCU .Carousel-module_hmf-carousel-header-title__whCvc {\n    -webkit-line-clamp: 1;\n    line-clamp: 1;\n  }\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-body__NJn-M {\n  box-sizing: border-box;\n  overflow: hidden;\n  position: relative;\n  width: 100%;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-content__cghf2 {\n  gap: var(--hmf-xs);\n  transition: transform 0.3s ease;\n}\n@media (min-width: 768px) {\n  .Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-content__cghf2:hover .Carousel-module_hmf-carousel-scroll-button__KdJJa {\n    display: block;\n  }\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-content-scroll__TxkxX {\n  overflow: hidden;\n  -ms-overflow-style: none; /* IE and Edge */\n  scroll-behavior: smooth;\n  scroll-snap-type: x mandatory;\n  scrollbar-width: none; /* Firefox */\n  /* Hide scrollbar for Chrome, Safari and Opera */\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-content-scroll__TxkxX::-webkit-scrollbar {\n  display: none;\n  width: 0;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-content-scroll__TxkxX::-webkit-scrollbar-track {\n  background: none;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-content-scroll__TxkxX::-webkit-scrollbar-thumb {\n  background: none;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-scroll-button__KdJJa {\n  border: 1px solid var(--hmf-gray900);\n  border-radius: 22px;\n  cursor: pointer;\n  line-height: 0px;\n  top: calc(50% - 22px);\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-scroll-button-right__Nabej {\n  margin-left: -60px;\n  right: var(--hmf-xs);\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-carousel-scroll-button-left__Szg2C {\n  left: var(--hmf-xs);\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-skip-link__UXIXK {\n  border: 1px solid var(--hmf-border-inverse);\n  height: 1px;\n  left: -1000px;\n  width: 1px;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-skip-link__UXIXK:focus {\n  display: block;\n  height: unset;\n  left: unset;\n  width: -moz-fit-content;\n  width: fit-content;\n  z-index: 1;\n}\n.Carousel-module_hmf-carousel__DlI3O .Carousel-module_hmf-skip-link__UXIXK:focus-visible {\n  box-sizing: border-box;\n  outline: 1px dashed var(--hmf-border-focused);\n}";
var styles$2 = {"hmf-carousel":"Carousel-module_hmf-carousel__DlI3O","hmf-carousel-header":"Carousel-module_hmf-carousel-header__JMMCU","hmf-carousel-header-title":"Carousel-module_hmf-carousel-header-title__whCvc","hmf-carousel-header-subtitle":"Carousel-module_hmf-carousel-header-subtitle__L48GQ","hmf-notitle":"Carousel-module_hmf-notitle__H-C6e","hmf-carousel-header-action-button":"Carousel-module_hmf-carousel-header-action-button__295s-","hmf-carousel-body":"Carousel-module_hmf-carousel-body__NJn-M","hmf-carousel-content":"Carousel-module_hmf-carousel-content__cghf2","hmf-carousel-scroll-button":"Carousel-module_hmf-carousel-scroll-button__KdJJa","hmf-carousel-content-scroll":"Carousel-module_hmf-carousel-content-scroll__TxkxX","hmf-carousel-scroll-button-right":"Carousel-module_hmf-carousel-scroll-button-right__Nabej","hmf-carousel-scroll-button-left":"Carousel-module_hmf-carousel-scroll-button-left__Szg2C","hmf-skip-link":"Carousel-module_hmf-skip-link__UXIXK"};
styleInject(css_248z$2);

const Carousel = ({
  className = '',
  showHeader = true,
  showTitle = true,
  showSubtitle = true,
  showActionButton = true,
  showScrollButtons = true,
  showScrollbar = false,
  showScrollButtonsOnMobile = false,
  title = '',
  subtitle = '',
  actionButtonTitle = '',
  onActionButtonClick,
  children
}) => {
  const pixelBuffer = 20; // 20px buffer
  const observerRef = useRef(null);
  const [carouselContentRef, setCarouselContentRef] = useState(null);
  const [showScrollRight, setShowScrollRight] = useState(true);
  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [showScroll, setShowScroll] = useState(showScrollbar);
  useEffect(() => {
    setShowScroll(showScrollbar);
  }, [showScrollbar]);
  useEffect(() => {
    if (carouselContentRef) {
      handleShowOverflow();
      // Set up a MutationObserver to detect changes to the carousel content
      observerRef.current = new MutationObserver(() => {
        // Check if the carousel content is overflowing
        handleShowOverflow();
      });
      // Observe the carousel content for changes
      observerRef.current.observe(carouselContentRef, {
        childList: true,
        subtree: true
      });
      carouselContentRef.addEventListener('scroll', checkScrollPositions);
      window.addEventListener('resize', handleShowOverflow);
      // Cleanup function to remove event listeners and observer
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
        carouselContentRef.removeEventListener('scroll', checkScrollPositions);
        window.removeEventListener('resize', handleShowOverflow);
      };
    }
  }, [carouselContentRef]);
  const onCarouselContentRefChange = useCallback(node => {
    if (node) {
      setCarouselContentRef(node);
    }
  }, []);
  /**
   * @description Checks the scroll positions of the carousel content.
   * If the scroll position is at the end of the content, hide the scroll button for that direction.
   */
  const checkScrollPositions = () => {
    if (carouselContentRef) {
      // Check if the scroll position is at the end of the carousel content
      setShowScrollLeft(carouselContentRef.scrollLeft - pixelBuffer > 0); // 20px buffer
      // Check if the scroll position is at the beginning of the carousel content
      setShowScrollRight(carouselContentRef.scrollLeft < carouselContentRef.scrollWidth - carouselContentRef.clientWidth - pixelBuffer // 20px buffer
      );
    }
  };
  /**
   * @description Handles showing the overflow of the carousel content.
   * If the content is wider than the container, show the scrollbar and the scroll buttons.
   */
  const handleShowOverflow = () => {
    let carouselContent;
    if (carouselContentRef) {
      carouselContent = carouselContentRef;
    } else {
      return;
    }
    // Get the width of the content and the container
    const contentWidth = carouselContent.clientWidth ?? 0;
    const containerWidth = carouselContent.scrollWidth ?? 0;
    // Show scrollbar if content is wider than container by 20px
    const scrollable = containerWidth - contentWidth > pixelBuffer; // 20px buffer
    setShowScroll(scrollable);
    // If scrollbar is shown, check if it's at the end
    setShowScrollRight(scrollable && carouselContent.scrollLeft < containerWidth - contentWidth);
    setShowScrollLeft(scrollable && carouselContent.scrollLeft > 0);
    // Set overflow style based on showScrollbar
    carouselContent.style.overflowX = scrollable ? 'scroll' : 'hidden';
    if (scrollable) {
      // Get carousel content children
      for (const child of carouselContent.children) {
        // Add scroll snap start to each child
        child.style.scrollSnapAlign = 'start';
        child.style.scrollMarginLeft = pixelBuffer + 'px'; // 20px buffer
      }
    }
  };
  /**
   * @description Scrolls the carousel content to the right by 200px.
   */
  const scrollRight = () => {
    if (carouselContentRef) {
      carouselContentRef.scrollLeft += 200;
    }
    // If you can scroll right, you can scroll left
    setShowScrollLeft(true);
  };
  /**
   * @description Scrolls the carousel content to the left by 200px.
   */
  const scrollLeft = () => {
    if (carouselContentRef) {
      carouselContentRef.scrollLeft -= 200;
    }
    // If you can scroll left, you can scroll right
    setShowScrollRight(true);
  };
  return jsxs("div", {
    className: `${styles$2['hmf-carousel']} hmf-carousel ${className}`,
    children: [showHeader && jsxs("div", {
      className: `${styles$2['hmf-carousel-header']} hmf-carousel-header hmf-p-s`,
      children: [showTitle && jsx("div", {
        className: `${styles$2['hmf-carousel-header-title']} hmf-carousel-header-title hmf-header-bold-l ${subtitle && showSubtitle && 'hmf-mb-xxs'}`,
        children: title
      }), showSubtitle && jsx("div", {
        className: `${styles$2['hmf-carousel-header-subtitle']} hmf-carousel-header-subtitle ${(!title || !showTitle) && `${styles$2['hmf-notitle']}`} hmf-body-m`,
        children: subtitle
      }), showActionButton && jsx("div", {
        className: `${styles$2['hmf-carousel-header-action-button']} hmf-carousel-header-action-button hmf-body-m hmf-ml-m hmf-display-flex hmf-align-items-center hmf-justify-content-flex-end`,
        role: "button",
        onClick: onActionButtonClick,
        children: actionButtonTitle
      })]
    }), jsxs("div", {
      className: `${styles$2['hmf-carousel-body']} hmf-carousel-body hmf-py-s`,
      children: [jsx("div", {
        ref: onCarouselContentRefChange,
        className: `${styles$2['hmf-carousel-content']} ${styles$2['hmf-carousel-content-scroll']} hmf-carousel-content hmf-carousel-content-scroll hmf-position-relative hmf-display-flex hmf-align-items-center hmf-px-s hmf-py-xxs`,
        children: children
      }), showScrollLeft && showScrollButtons && jsx("button", {
        className: `${styles$2['hmf-carousel-scroll-button']} ${styles$2['hmf-carousel-scroll-button-left']} hmf-carousel-scroll-button hmf-carousel-scroll-button-left ${!showScrollButtonsOnMobile && 'hmf-display-none hmf-display-md-block'}`,
        "data-testid": "hmf-carousel-scroll-button-left",
        onClick: scrollLeft,
        children: jsx(HomefieldIconChevronLeft, {
          width: "16px",
          height: "16px",
          name: "chevron-left"
        })
      }), showScrollRight && showScrollButtons && jsx("button", {
        className: `${styles$2['hmf-carousel-scroll-button']} ${styles$2['hmf-carousel-scroll-button-right']} hmf-carousel-scroll-button hmf-carousel-scroll-button-right ${!showScrollButtonsOnMobile && 'hmf-display-none hmf-display-md-block'}`,
        "data-testid": "hmf-carousel-scroll-button-right",
        onClick: scrollRight,
        children: jsx(HomefieldIconChevronRight, {
          width: "16px",
          height: "16px",
          name: "chevron-right"
        })
      })]
    }), showScroll && jsx(Scrollbar, {
      scrollContent: carouselContentRef
    })]
  });
};

var css_248z$1 = "/** @format */\n/* stylelint-disable length-zero-no-unit */\n/* stylelint-disable length-zero-no-unit */\n/* stylelint-disable length-zero-no-unit */\n/** @format */\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN {\n  box-sizing: border-box;\n  max-width: 100vw;\n  width: 100%;\n  /* Carousel Header */\n  /* Carousel Body */\n  /* Carousel Controls */\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d {\n  box-sizing: border-box;\n  display: grid;\n  grid-gap: 0;\n  grid-template-columns: auto min-content;\n  grid-template-rows: min-content min-content;\n  max-height: 132px;\n  width: 100%;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-title__mwv4R {\n  grid-area: 1/1/2/2;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-subtitle__-H8VL {\n  grid-area: 2/1/3/3;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-subtitle__-H8VL.PaginationCarousel-module_hmf-notitle__ER-ek {\n  grid-area: 1/1/2/3;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-action-button__PSns1 {\n  cursor: pointer;\n  grid-area: 1/2/2/3;\n  white-space: nowrap;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-action-button__PSns1:hover {\n  text-decoration: underline;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-title__mwv4R, .PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-subtitle__-H8VL {\n  -webkit-box-orient: vertical;\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  line-clamp: 2;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  word-wrap: break-word;\n}\n@media (min-width: 768px) {\n  .PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d {\n    max-height: 101px;\n  }\n  .PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-subtitle__-H8VL {\n    grid-area: 2/1/3/2;\n  }\n  .PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-action-button__PSns1 {\n    grid-area: 1/2/3/3;\n  }\n  .PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d .PaginationCarousel-module_hmf-pagination-carousel-header-title__mwv4R {\n    -webkit-line-clamp: 1;\n    line-clamp: 1;\n  }\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK {\n  box-sizing: border-box;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm {\n  max-height: 600px;\n  overflow-x: auto;\n  scrollbar-width: none;\n  /* only for angular */\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm > * {\n  flex-shrink: 0;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm::ng-deep > * {\n  flex-shrink: 0;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm::-webkit-scrollbar {\n  display: none;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm.PaginationCarousel-module_scroll-in__ZNCA8 {\n  /* only for angular */\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm.PaginationCarousel-module_scroll-in__ZNCA8::ng-deep > * {\n  animation: PaginationCarousel-module_scroll-in__ZNCA8 600ms ease;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm.PaginationCarousel-module_scroll-in__ZNCA8 > * {\n  animation: PaginationCarousel-module_scroll-in__ZNCA8 600ms ease;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm.PaginationCarousel-module_scroll-out__9vJE0 {\n  /* only for angular */\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm.PaginationCarousel-module_scroll-out__9vJE0::ng-deep > * {\n  animation: PaginationCarousel-module_scroll-out__9vJE0 600ms ease;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK .PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm.PaginationCarousel-module_scroll-out__9vJE0 > * {\n  animation: PaginationCarousel-module_scroll-out__9vJE0 600ms ease;\n}\n@keyframes PaginationCarousel-module_scroll-in__ZNCA8 {\n  from {\n    transform: translateX(100%);\n  }\n  to {\n    transform: translateX(0%);\n  }\n}\n@keyframes PaginationCarousel-module_scroll-out__9vJE0 {\n  from {\n    transform: translateX(-100%);\n  }\n  to {\n    transform: translateX(0%);\n  }\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi {\n  justify-content: space-between;\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-dots__RwrEH {\n  gap: var(--hmf-xxxs);\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-dots__RwrEH .PaginationCarousel-module_pagination-dot-box__8BIT5 {\n  background-color: var(--hmf-gray200);\n  border-radius: var(--hmf-xs);\n  cursor: pointer;\n  height: var(--hmf-xs);\n  width: var(--hmf-xs);\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-dots__RwrEH .PaginationCarousel-module_pagination-dot-box__8BIT5 .PaginationCarousel-module_progress-bar__6BCu5 {\n  background-color: var(--hmf-secondary500);\n  border-radius: var(--hmf-xs);\n  height: var(--hmf-xs);\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-dots__RwrEH .PaginationCarousel-module_pagination-dot-box__8BIT5 .PaginationCarousel-module_progress-bar__6BCu5.PaginationCarousel-module_expanded__Q6dR2 {\n  width: var(--hmf-xl);\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-dots__RwrEH .PaginationCarousel-module_pagination-dot-box__8BIT5.PaginationCarousel-module_expanded__Q6dR2 {\n  width: var(--hmf-xl);\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-dots__RwrEH .PaginationCarousel-module_pause-play-button__5D6jX {\n  background-color: transparent;\n  border: 1px solid var(--hmf-gray900);\n  border-radius: var(--hmf-xxxs);\n  cursor: pointer;\n  height: var(--hmf-m);\n  padding-top: 3px;\n  width: var(--hmf-l);\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-buttons__qQ5FX {\n  gap: var(--hmf-xxxs);\n}\n.PaginationCarousel-module_hmf-pagination-carousel__nLvnN .PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi .PaginationCarousel-module_pagination-buttons__qQ5FX button {\n  background-color: var(--hmf-gray50);\n  border: 1px solid var(--hmf-gray900);\n  border-radius: 22px;\n  cursor: pointer;\n  line-height: 0px;\n  padding: 13px;\n}";
var styles$1 = {"hmf-pagination-carousel":"PaginationCarousel-module_hmf-pagination-carousel__nLvnN","hmf-pagination-carousel-header":"PaginationCarousel-module_hmf-pagination-carousel-header__WWE7d","hmf-pagination-carousel-header-title":"PaginationCarousel-module_hmf-pagination-carousel-header-title__mwv4R","hmf-pagination-carousel-header-subtitle":"PaginationCarousel-module_hmf-pagination-carousel-header-subtitle__-H8VL","hmf-notitle":"PaginationCarousel-module_hmf-notitle__ER-ek","hmf-pagination-carousel-header-action-button":"PaginationCarousel-module_hmf-pagination-carousel-header-action-button__PSns1","hmf-pagination-carousel-body":"PaginationCarousel-module_hmf-pagination-carousel-body__TFrHK","hmf-pagination-carousel-content":"PaginationCarousel-module_hmf-pagination-carousel-content__l1Pvm","scroll-in":"PaginationCarousel-module_scroll-in__ZNCA8","scroll-out":"PaginationCarousel-module_scroll-out__9vJE0","hmf-pagination-carousel-controls":"PaginationCarousel-module_hmf-pagination-carousel-controls__u6CQi","pagination-dots":"PaginationCarousel-module_pagination-dots__RwrEH","pagination-dot-box":"PaginationCarousel-module_pagination-dot-box__8BIT5","progress-bar":"PaginationCarousel-module_progress-bar__6BCu5","expanded":"PaginationCarousel-module_expanded__Q6dR2","pause-play-button":"PaginationCarousel-module_pause-play-button__5D6jX","pagination-buttons":"PaginationCarousel-module_pagination-buttons__qQ5FX"};
styleInject(css_248z$1);

const buildPromiseFromImageEvent = (image, event) => {
  return new Promise(resolve => image.addEventListener(event, resolve, {
    passive: true,
    once: true
  }));
};
const getImagesLoadingStatus = images => {
  const onLoadPromises = [];
  images.forEach(img => {
    const newOnLoadPromise = buildPromiseFromImageEvent(img, 'load');
    onLoadPromises.push(newOnLoadPromise);
  });
  return Promise.allSettled(onLoadPromises);
};
const PaginationCarousel = ({
  className = '',
  showHeader = true,
  showTitle = true,
  showSubtitle = true,
  showActionButton = true,
  title = '',
  subtitle = '',
  actionButtonTitle = '',
  onActionButtonClick,
  children,
  autoPlay = true,
  gap = 12
}) => {
  let swipeStartCoord = [0, 0];
  let swipeStartTime = 0;
  let progressCounter = 0;
  const carouselContentRef = useRef(null);
  const [intervalID, setIntervalID] = useState();
  const [autoPlayPaused, setAutoPlayPaused] = useState(false);
  const [autoPlayProgress, setAutoPlayProgress] = useState(0);
  const [elementsPerPage, setElementsPerPage] = useState(1);
  const [totalElements, setTotalElements] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [scrollIn, setScrollIn] = useState(false);
  const [scrollOut, setScrollOut] = useState(false);
  useEffect(() => {
    const carouselContainer = carouselContentRef.current;
    if (carouselContainer) {
      const carouselElements = Array.from(carouselContainer.children);
      const imagesInCarousel = Array.from(carouselContainer.getElementsByTagName('img'));
      // If there are images not loaded inside the carousel content that affect the width of each element, the scroll calculation is not correct
      // This service is implemented to ensure the images are loaded and then calculate the scroll and pagination properly
      if (imagesInCarousel.length === 0) {
        initPaginationSettings(carouselContainer, carouselElements);
      } else {
        getImagesLoadingStatus(imagesInCarousel).then(() => {
          initPaginationSettings(carouselContainer, carouselElements);
        });
      }
    }
  }, []);
  useEffect(() => {
    const currentElementIndex = elementsPerPage * currentPage;
    carouselContentRef.current?.scroll({
      left: carouselContentRef.current.scrollWidth / totalElements * currentElementIndex,
      behavior: 'smooth'
    });
  }, [currentPage, elementsPerPage, totalElements]);
  useEffect(() => {
    carouselContentRef.current?.addEventListener('touchstart', swipeStart, {
      passive: false
    });
    carouselContentRef.current?.addEventListener('touchend', swipeEnd, {
      passive: false
    });
    return () => {
      carouselContentRef.current?.removeEventListener('touchstart', swipeStart);
      carouselContentRef.current?.removeEventListener('touchend', swipeEnd);
    };
  });
  useEffect(() => {
    const handleResize = () => {
      if (autoPlay) {
        pause();
      }
      const carouselContainer = carouselContentRef.current;
      if (carouselContainer) {
        const carouselElements = Array.from(carouselContainer.children);
        initPaginationSettings(carouselContainer, carouselElements);
        carouselContentRef.current.scroll({
          left: 0,
          behavior: 'smooth'
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });
  useEffect(() => {
    const autoPlayCallback = () => {
      progressCounter++;
      if (progressCounter > 100) {
        progressCounter = 0;
      }
      setAutoPlayProgress(progressCounter);
    };
    if (autoPlay && !autoPlayPaused) {
      const intervalID = setInterval(autoPlayCallback, 40);
      setIntervalID(intervalID);
    }
    return () => {
      if (autoPlay && !autoPlayPaused) {
        clearInterval(intervalID);
      }
    };
  }, [autoPlayPaused]);
  useEffect(() => {
    if (autoPlayProgress === 100) {
      next();
    }
  }, [autoPlayProgress]);
  const next = () => {
    const newCurrentPage = currentPage + 1;
    if (newCurrentPage + 1 > totalPages) {
      // scroll back to first page
      setCurrentPage(0);
      setScrollIn(true);
      if (carouselContentRef.current) {
        carouselContentRef.current.scrollLeft = 0;
      }
    } else {
      setScrollIn(false);
      setScrollOut(false);
      setCurrentPage(newCurrentPage);
    }
  };
  const previous = () => {
    const newCurrentPage = currentPage - 1;
    if (newCurrentPage < 0) {
      // scroll forward to last page
      setCurrentPage(totalPages - 1);
      setScrollOut(true);
      if (carouselContentRef.current) {
        carouselContentRef.current.scrollLeft = carouselContentRef.current.scrollWidth / totalElements * totalElements - 1;
      }
    } else {
      setScrollIn(false);
      setScrollOut(false);
      setCurrentPage(newCurrentPage);
    }
  };
  const selectPage = pageIndex => {
    setCurrentPage(pageIndex);
  };
  const pause = () => {
    setAutoPlayPaused(true);
    setAutoPlayProgress(50);
    clearInterval(intervalID);
  };
  const play = () => {
    setAutoPlayPaused(false);
  };
  const isElementVisible = (elementRect, containerRect) =>
  // 'gap' variable is the gap between elements, which is needed to account for a correct scroll
  // if changed, make sure to change the margin right of each element in .scss file as well
  elementRect.left >= containerRect.left - gap && elementRect.right <= containerRect.right + gap;
  const initPaginationSettings = (carouselContainer, carouselElements) => {
    const containerRect = carouselContainer.getBoundingClientRect();
    let elementCounter = 0;
    carouselElements.forEach(el => {
      const elementRect = el.getBoundingClientRect();
      if (isElementVisible(elementRect, containerRect)) {
        elementCounter++;
      }
    });
    setTotalElements(carouselElements.length);
    setElementsPerPage(elementCounter || 1);
    setTotalPages(Math.ceil(carouselElements.length / (elementCounter || 1)));
    setCurrentPage(0);
  };
  const swipeStart = e => {
    e.preventDefault();
    if (autoPlay) {
      pause();
    }
    swipeStartCoord = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
    swipeStartTime = new Date().getTime();
  };
  const swipeEnd = e => {
    e.preventDefault();
    const coord = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
    const time = new Date().getTime();
    const direction = [coord[0] - swipeStartCoord[0], coord[1] - swipeStartCoord[1]];
    const duration = time - swipeStartTime;
    if (duration < 1000 && Math.abs(direction[0]) > 30 &&
    // Long enough
    Math.abs(direction[0]) > Math.abs(direction[1] * 3)) {
      // Horizontal enough
      direction[0] < 0 ? next() : previous();
    }
  };
  return jsxs("div", {
    className: `${styles$1['hmf-pagination-carousel']} hmf-pagination-carousel ${className}`,
    children: [showHeader && jsxs("div", {
      className: `${styles$1['hmf-pagination-carousel-header']} hmf-pagination-carousel-header hmf-p-xs`,
      children: [showTitle && jsx("div", {
        className: `${styles$1['hmf-pagination-carousel-header-title']} hmf-pagination-carousel-header-title hmf-header-bold-l ${subtitle && showSubtitle && 'hmf-mb-xxs'}`,
        children: title
      }), showSubtitle && jsx("div", {
        className: `${styles$1['hmf-pagination-carousel-header-subtitle']} hmf-pagination-carousel-header-subtitle ${(!title || !showTitle) && `${styles$1['hmf-notitle']}`} hmf-body-m`,
        children: subtitle
      }), showActionButton && jsx("div", {
        className: `${styles$1['hmf-pagination-carousel-header-action-button']} hmf-pagination-carousel-header-action-button hmf-body-m hmf-ml-m hmf-display-flex hmf-align-items-center hmf-justify-content-flex-end`,
        role: "button",
        onClick: onActionButtonClick,
        children: actionButtonTitle
      })]
    }), jsx("div", {
      className: `${styles$1['hmf-pagination-carousel-body']} hmf-pagination-carousel-body hmf-px-xs`,
      children: jsx("div", {
        ref: carouselContentRef,
        style: {
          gap: gap + 'px'
        },
        className: `${styles$1['hmf-pagination-carousel-content']} hmf-pagination-carousel-content hmf-display-flex hmf-position-relative
          ${scrollIn === true && `${styles$1['scroll-in']}`} ${scrollOut === true && `${styles$1['scroll-out']}`}`,
        children: children
      })
    }), jsxs("div", {
      className: `${styles$1['hmf-pagination-carousel-controls']} hmf-pagination-carousel-controls hmf-display-flex hmf-p-xs`,
      children: [jsxs("div", {
        className: `${styles$1['pagination-dots']} hmf-display-flex hmf-align-items-center`,
        children: [Array.from(Array(totalPages).keys()).map(index => {
          return jsx("div", {
            className: `${styles$1['pagination-dot-box']} pagination-dot-box ${index === currentPage && `${styles$1['expanded']}`}`,
            onClick: () => selectPage(index),
            children: index === currentPage && jsx("div", {
              className: `${styles$1['progress-bar']}`,
              style: {
                width: `${autoPlay ? autoPlayProgress + '%' : '50%'}`
              }
            })
          }, 'pagination-dot-' + index);
        }), autoPlay && !autoPlayPaused && jsx("button", {
          className: `${styles$1['pause-play-button']}`,
          onClick: pause,
          children: jsx(HomefieldIconMediaPause, {
            width: "16px",
            height: "16px",
            name: "media-pause"
          })
        }), autoPlay && autoPlayPaused && jsx("button", {
          className: `${styles$1['pause-play-button']}`,
          onClick: play,
          children: jsx(HomefieldIconMediaPlay, {
            width: "16px",
            height: "16px",
            name: "media-play"
          })
        })]
      }), jsxs("div", {
        className: `${styles$1['pagination-buttons']} hmf-display-flex hmf-pr-xs`,
        children: [jsx("button", {
          onClick: previous,
          children: jsx(HomefieldIconChevronLeft, {
            width: "16px",
            height: "16px",
            name: "chevron-left"
          })
        }), jsx("button", {
          onClick: next,
          children: jsx(HomefieldIconChevronRight, {
            width: "16px",
            height: "16px",
            name: "chevron-right"
          })
        })]
      })]
    })]
  });
};

var css_248z = "/* stylelint-disable length-zero-no-unit */\n/* stylelint-disable length-zero-no-unit */\n/* stylelint-disable length-zero-no-unit */\n/** @format */\n.ProductCard-module_homefield-productcard__cgxTr {\n  background-color: transparent;\n  border: 1px solid transparent;\n  color: var(--hmf-gray900) !important;\n  display: flex;\n  overflow: hidden;\n  text-decoration: none;\n}\n.ProductCard-module_homefield-productcard__cgxTr:visited:not(.ProductCard-module_loading__Jg2LG) {\n  color: var(--hmf-gray900);\n}\n.ProductCard-module_homefield-productcard__cgxTr:hover:not(.ProductCard-module_homefield-productcard-skeleton__xt8iU), .ProductCard-module_homefield-productcard__cgxTr:active:not(.ProductCard-module_homefield-productcard-skeleton__xt8iU) {\n  box-shadow: var(--hmf-subtle-shadow);\n}\n.ProductCard-module_homefield-productcard__cgxTr:focus:not(.ProductCard-module_homefield-productcard-skeleton__xt8iU), .ProductCard-module_homefield-productcard__cgxTr:focus-visible:not(.ProductCard-module_homefield-productcard-skeleton__xt8iU) {\n  border: 1px dashed #797979;\n  border-radius: 4px;\n  outline: none;\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-image__pJhql {\n  border-radius: 4px;\n  height: 100%;\n  width: auto;\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-image-wrapper__gvZ77 {\n  display: flex;\n  justify-content: center;\n  margin: 0;\n  overflow: hidden;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G {\n  flex-direction: column;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-small__tOcr4 {\n  flex: 0 0 136px;\n  min-width: 136px;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-small__tOcr4 .ProductCard-module_homefield-productcard-image-wrapper__gvZ77,\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-small__tOcr4 .ProductCard-module_homefield-productcard-skeleton-image__Zwvb0 {\n  height: 120px;\n  margin: 0 auto;\n  width: 120px;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT {\n  flex: 1 0 120px;\n  min-width: 120px;\n}\n@media (min-width: 1280px) {\n  .ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT {\n    flex-basis: 190px;\n    min-width: 190px;\n  }\n}\n@media (min-width: 1440px) {\n  .ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT {\n    flex-basis: 220px;\n    min-width: 220px;\n  }\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-image-wrapper__gvZ77,\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-skeleton-image__Zwvb0 {\n  height: 104px;\n  margin: 0 auto;\n  width: 104px;\n}\n@media (min-width: 1280px) {\n  .ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-image-wrapper__gvZ77,\n  .ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-skeleton-image__Zwvb0 {\n    height: 176px;\n    width: 176px;\n  }\n}\n@media (min-width: 1440px) {\n  .ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-image-wrapper__gvZ77,\n  .ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-vertical__b0i-G.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-skeleton-image__Zwvb0 {\n    height: 204px;\n    width: 204px;\n  }\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-horizontal__f43t2.ProductCard-module_homefield-productcard-small__tOcr4 {\n  height: 106px;\n  max-width: 382px;\n  min-width: 300px;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-horizontal__f43t2.ProductCard-module_homefield-productcard-small__tOcr4 .ProductCard-module_homefield-productcard-image-wrapper__gvZ77,\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-horizontal__f43t2.ProductCard-module_homefield-productcard-small__tOcr4 .ProductCard-module_homefield-productcard-skeleton-image__Zwvb0 {\n  height: 100%;\n  width: 106px;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-horizontal__f43t2.ProductCard-module_homefield-productcard-large__EJ6BT {\n  height: 166px;\n  max-width: 496px;\n  min-width: 400px;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-horizontal__f43t2.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-image-wrapper__gvZ77,\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-horizontal__f43t2.ProductCard-module_homefield-productcard-large__EJ6BT .ProductCard-module_homefield-productcard-skeleton-image__Zwvb0 {\n  height: 100%;\n  width: 166px;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-horizontal__f43t2 .ProductCard-module_homefield-productcard-content__rIitT {\n  width: 100%;\n}\n.ProductCard-module_homefield-productcard__cgxTr.ProductCard-module_homefield-productcard-background__qexsq {\n  background-color: var(--hmf-gray50);\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-prices__pKDsc {\n  display: flex;\n  flex-wrap: wrap;\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-name__ObxQm {\n  -webkit-box-orient: vertical;\n  display: -webkit-box !important;\n  -webkit-line-clamp: 3;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: normal;\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-originalPrice__9-HAW {\n  text-decoration: line-through;\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-skeleton__xt8iU {\n  height: 16px;\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-skeleton-1__nrJXD {\n  width: 75px;\n}\n@media (min-width: 1280px) {\n  .ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-skeleton-1__nrJXD {\n    width: 75%;\n  }\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-skeleton-2__zkyNl {\n  width: 50px;\n}\n@media (min-width: 1280px) {\n  .ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-skeleton-2__zkyNl {\n    width: 50%;\n  }\n}\n.ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-skeleton-3__QNnEG {\n  width: 25px;\n}\n@media (min-width: 1280px) {\n  .ProductCard-module_homefield-productcard__cgxTr .ProductCard-module_homefield-productcard-skeleton-3__QNnEG {\n    width: 25%;\n  }\n}";
var styles = {"homefield-productcard":"ProductCard-module_homefield-productcard__cgxTr","loading":"ProductCard-module_loading__Jg2LG","homefield-productcard-skeleton":"ProductCard-module_homefield-productcard-skeleton__xt8iU","homefield-productcard-image":"ProductCard-module_homefield-productcard-image__pJhql","homefield-productcard-image-wrapper":"ProductCard-module_homefield-productcard-image-wrapper__gvZ77","homefield-productcard-vertical":"ProductCard-module_homefield-productcard-vertical__b0i-G","homefield-productcard-small":"ProductCard-module_homefield-productcard-small__tOcr4","homefield-productcard-skeleton-image":"ProductCard-module_homefield-productcard-skeleton-image__Zwvb0","homefield-productcard-large":"ProductCard-module_homefield-productcard-large__EJ6BT","homefield-productcard-horizontal":"ProductCard-module_homefield-productcard-horizontal__f43t2","homefield-productcard-content":"ProductCard-module_homefield-productcard-content__rIitT","homefield-productcard-background":"ProductCard-module_homefield-productcard-background__qexsq","homefield-productcard-prices":"ProductCard-module_homefield-productcard-prices__pKDsc","homefield-productcard-name":"ProductCard-module_homefield-productcard-name__ObxQm","homefield-productcard-originalPrice":"ProductCard-module_homefield-productcard-originalPrice__9-HAW","homefield-productcard-skeleton-1":"ProductCard-module_homefield-productcard-skeleton-1__nrJXD","homefield-productcard-skeleton-2":"ProductCard-module_homefield-productcard-skeleton-2__zkyNl","homefield-productcard-skeleton-3":"ProductCard-module_homefield-productcard-skeleton-3__QNnEG"};
styleInject(css_248z);

const ProductCard = ({
  link,
  target,
  image,
  alt,
  name,
  price,
  originalPrice,
  variant,
  background,
  size,
  loading,
  className,
  ...props
}) => {
  return loading ? jsxs("article", {
    "data-testid": "product-card-skeleton",
    className: `${styles['homefield-productcard']} ${styles['homefield-productcard-skeleton']} homefield-productcard homefield-productcard-skeleton hmf-sharp-radius hmf-p-xxs ${styles[`homefield-productcard-${variant}`]} ${styles[`homefield-productcard-${size}`]} homefield-productcard-${variant} homefield-productcard-${size} ${className}`,
    children: [jsx(Skeleton, {
      className: `${styles['homefield-productcard-skeleton-image']} ${styles['homefield-productcard-image']}`
    }), jsxs("section", {
      className: `${styles['homefield-productcard-content']} ${variant === 'horizontal' ? 'hmf-ml-xxs' : ''}`,
      children: [jsx(Skeleton, {
        className: `${styles['homefield-productcard-skeleton-1']}`
      }), jsx(Skeleton, {
        className: `${styles['homefield-productcard-skeleton-2']} hmf-my-xxs`
      }), jsx(Skeleton, {
        className: `${styles['homefield-productcard-skeleton-3']}`
      })]
    })]
  }) : link && image && alt && name && price ? jsxs("a", {
    href: link,
    target: `_${target}`,
    "aria-label": "View Product Details",
    className: `${styles['homefield-productcard']} homefield-productcard hmf-sharp-radius hmf-p-xxs ${styles[`homefield-productcard-${variant}`]} ${styles[`homefield-productcard-${size}`]} homefield-productcard-${variant} homefield-productcard-${size} ${background ? `${styles['homefield-productcard-background']}` : ''} ${className}`,
    ...props,
    children: [jsx("figure", {
      className: `${styles['homefield-productcard-image-wrapper']} homefield-productcard-image-wrapper`,
      children: jsx("img", {
        className: `${styles['homefield-productcard-image']} homefield-productcard-image`,
        src: image,
        alt: alt
      })
    }), jsxs("section", {
      className: `${styles['homefield-productcard-content']} homefield-productcard-content ${variant === 'horizontal' ? 'hmf-ml-xxs' : ''}`,
      children: [jsx("p", {
        className: `${styles['homefield-productcard-name']} homefield-productcard-name hmf-header-xs hmf-mb-xxs ${variant === 'vertical' ? 'hmf-mt-xxs' : ''}`,
        children: name
      }), jsxs("p", {
        className: `${styles['homefield-productcard-prices']} homefield-productcard-prices`,
        children: [jsx("span", {
          className: `${styles['homefield-productcard-price']} homefield-productcard-price hmf-header-bold-xs hmf-pr-xxxs`,
          children: price
        }), originalPrice ? jsx("span", {
          className: `${styles['homefield-productcard-originalPrice']} homefield-productcard-originalPrice hmf-subheader-s`,
          children: originalPrice
        }) : null]
      })]
    })]
  }) : null;
};
ProductCard.defaultProps = {
  variant: 'vertical',
  background: true,
  size: 'large',
  loading: false,
  target: 'self'
};

export { Carousel, PaginationCarousel, ProductCard };
